import React, { useMemo } from 'react';
import { SEASONAL_CONFIGS, SEASONAL_ANIMATIONS, SeasonalConfig, SeasonalParticle } from '../../constants/seasonalConfigs';

const getCurrentDate = () => {
  const now = new Date();
  return {
    month: now.getMonth(),
    day: now.getDate(),
    dayOfWeek: now.getDay(),
  };
};

const generateParticles = (config: SeasonalConfig) => {
  const allParticles: Array<{
    id: string;
    left: number;
    delay: number;
    duration: number;
    size: number;
    emoji?: string;
    shape?: string;
    color: string;
    animation: string;
    opacity: number;
  }> = [];

  config.particles.forEach((particleType: SeasonalParticle, typeIndex: number) => {
    for (let i = 0; i < particleType.count; i++) {
      allParticles.push({
        id: `${typeIndex}-${i}`,
        left: Math.random() * 100,
        delay: Math.random() * 15,
        duration: 10 + Math.random() * 10,
        size: particleType.sizeRange[0] + Math.random() * (particleType.sizeRange[1] - particleType.sizeRange[0]),
        emoji: particleType.emoji,
        shape: particleType.shape,
        color: particleType.color,
        animation: particleType.animation,
        opacity: particleType.opacity,
      });
    }
  });

  return allParticles;
};

const renderShape = (shape: string | undefined, size: number, color: string) => {
  switch (shape) {
    case 'heart':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
        </svg>
      );
    case 'drop':
      return (
        <svg width={size} height={size * 1.5} viewBox="0 0 10 15" fill={color}>
          <path d="M5 0C5 0 0 6 0 9.5C0 12.5 2.2 15 5 15C7.8 15 10 12.5 10 9.5C10 6 5 0 5 0Z" />
        </svg>
      );
    case 'leaf':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7 8 17 8 17 8Z" />
        </svg>
      );
    case 'star':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
        </svg>
      );
    case 'circle':
    default:
      return (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      );
  }
};

interface SeasonalBackgroundProps {
  enabled?: boolean;
}

export const SeasonalBackground: React.FC<SeasonalBackgroundProps> = ({ enabled = true }) => {
  const { month } = getCurrentDate();
  const config = SEASONAL_CONFIGS[month];

  const particles = useMemo(() => generateParticles(config), [config]);

  if (!enabled) return null;

  return (
    <>
      <style>{SEASONAL_ANIMATIONS}</style>

      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ zIndex: 0 }}
        aria-hidden="true"
      >
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute"
            style={{
              left: `${particle.left}%`,
              top: particle.animation === 'floatUp' ? 'auto' : '-20px',
              bottom: particle.animation === 'floatUp' ? '-20px' : 'auto',
              opacity: particle.opacity,
              animation: `${particle.animation} ${particle.duration}s linear infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          >
            {particle.emoji ? (
              <span style={{ fontSize: particle.size }}>{particle.emoji}</span>
            ) : (
              renderShape(particle.shape, particle.size, particle.color)
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default SeasonalBackground;
